<script setup lang="ts">
import { inject, Ref, ref, toRaw, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import i18n, { getCurrentLocale } from '@/i18n';
import { useDebounceFn } from '@vueuse/core';
import { useDialog } from 'primevue/usedialog';
import { useWorksheetComposable } from '@/components/modal/worksheet/useWorksheetComposable';
import { formatDate } from '@/utils/dateUtils';
import { FilterMatchMode } from '@primevue/core/api';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import HistoryViewMoreModal from '@/components/modal/HistoryViewMoreModal.vue';

const dialogRef = inject('dialogRef') as Ref;
const historyData = dialogRef?.value?.data?.historyData;
const quoteId = dialogRef?.value?.data?.quoteId;
const onClose = dialogRef?.value?.options.emits.onClose;

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const currentLanguage = getCurrentLocale();
const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);
const searchValue = ref('');
const filters = ref();
const cloneHistory = ref<any>([]);
const columns = ref([
  { field: 'changeAction', header: t('common.action') },
  { field: 'changeField', header: t('history_log.change') },
  { field: 'user', header: t('proof_pricing.user') },
  { field: 'createdDate', header: t('history_log.timestamp') }
]);

const { getCabinName } = useWorksheetComposable();

const initFilters = () => {
  filters.value = {
    global: { value: searchValue.value, matchMode: FilterMatchMode.CONTAINS }
  };
};
initFilters();

const getClonetHistory = () => {
  return historyData.map((history: any) => {
    cloneHistory.value.push({
      ...structuredClone(toRaw(history)),
      changeField: convertString(history.oldValue, history.newValue, history.action),
      changeAction: convertActionOption(history.quoteOption) + convertAction(history)
    });
  });
};

const convertString = (oldValue: string, newValue: string, action: string) => {
  if (oldValue || newValue) {
    const oldData = checkCabin(convertDateString(replaceEmptyValue(oldValue)), action);
    const newData = checkCabin(convertDateString(replaceEmptyValue(newValue)), action);
    return `${t('worksheet.from')} "${oldData}" ${t('worksheet.to')} "${newData}"`;
  } else {
    return '';
  }
};

const checkCabin = (changeValue: string, action: string) => {
  if (action?.toLowerCase() === 'flight_details_cabin') {
    return getCabinName(changeValue) ?? changeValue;
  }
  return changeValue;
};

const convertAction = (quoteData: any) => {
  if (quoteData) {
    const getHotelCode = quoteData.hotelCode ? checkCabinCategory(quoteData) + '-' : '';
    const getRoomCode = quoteData.roomCode ? quoteData.roomCode + '-' : '';
    const getOccType = quoteData.occType ? checkOccType(quoteData) + ' ' : '';
    const actionString = quoteData.action ? t(`history_log.${quoteData.action.toLowerCase()}`) : '';
    return getHotelCode + getRoomCode + getOccType + actionString;
  }
};

const convertActionOption = (quoteOption: string) => {
  return quoteOption ? t('book_form.option') + ' ' + quoteOption + ': ' : '';
};

const checkCabinCategory = (quoteData: any) => {
  const getCruiseAction = quoteData.action.toLowerCase();
  if (getCruiseAction.includes('cruise')) {
    switch (quoteData.hotelCode) {
      case 'IN':
        return t('advance_quote.form.inside');
      case 'OS':
        return t('advance_quote.form.outside');
      case 'BA':
        return t('advance_quote.form.balcony');
      case 'SU':
        return t('advance_quote.form.suite');
      default:
        return quoteData.hotelCode;
    }
  } else {
    return quoteData.hotelCode;
  }
};

const checkOccType = (quoteData: any) => {
  if (quoteData.occType.toLowerCase() in i18n.global.messages.value.en.history_log) {
    return t(`history_log.${quoteData.occType.toLowerCase()}`);
  } else {
    return quoteData.occType;
  }
};

const convertDateString = (dateString: string) => {
  if (checkChangeTime(dateString)) {
    return formatDate(dateString, 'dd-LLL-yyyy', true, currentLanguage);
  }

  return dateString;
};

const checkChangeTime = (dateString: string) => {
  if (!isValidISODate(dateString)) {
    return false;
  }
  const normalizedDateString = dateString.replace(/\.\d+$/, '');
  const date = new Date(normalizedDateString);
  return !isNaN(date.getTime());
};

const isValidISODate = (dateString: string): boolean => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const timeRegex = /^T\d{2}:\d{2}(:\d{2}(\.\d+)?)?([Z+-]\d{2}:\d{2})?$/;

  const [datePart, timePartWithZone] = dateString.split('T');

  if (!dateRegex.test(datePart)) {
    return false;
  }
  if (!timePartWithZone) {
    return true;
  }

  return timeRegex.test(`T${timePartWithZone}`);
};

const convertTimeStampToDate = (timestamp: string) => {
  return formatDate(timestamp, 'dd-LLL-yyyy hh:mm:ss', true, currentLanguage);
};

const openViewMoreHistory = (oldVal: string, newVal: string, changeAction: string) => {
  const historyChange = {
    oldValue: replaceEmptyValue(oldVal),
    newValue: replaceEmptyValue(newVal)
  };
  const dialogRef = dialog.open(HistoryViewMoreModal, {
    data: {
      historyChange: historyChange,
      headerTitle: changeAction
    },
    props: {
      header: t('management.log.extended_history') + ' - ' + quoteId,
      modal: true,
      style: {
        width: '75vw'
      }
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const filterQuote = useDebounceFn(() => {
  filters.value['global'].value = searchValue.value;
}, 400);

const replaceEmptyValue = (historyText: string) => {
  return historyText === 'null' || historyText === '' ? ' ' : historyText;
};

const checkChangeContent = (changeContent: string) => {
  const startsWithTag = /^<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/;
  const endsWithTag = /<\/([a-zA-Z][a-zA-Z0-9]*)>$/;
  return startsWithTag.test(changeContent) && endsWithTag.test(changeContent);
};

onMounted(() => {
  getClonetHistory();
});
</script>
<template>
  <div class="history-modal flex justify-content-center align-items-center flex-column">
    <div class="flex justify-content-end align-items-center pt-2 pb-3 w-full">
      <span class="p-input-icon-left">
        <i class="pi pi-search" />
        <InputText id="keywordSearch" v-model="searchValue" @input="filterQuote" :placeholder="$t('my_quote.search_keyword')" />
      </span>
    </div>
    <DataTable :value="cloneHistory" paginator v-model:filters="filters" :rowsPerPageOptions="rowsPerPageOptions" :rows="rowsPerPageOptions[0]" class="w-full">
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <TableColumn v-for="column of columns" :key="column.field" :field="column.field" :class="column.field === 'changeField' ? 'w-5' : ''">
        <template #header>
          <span>
            {{ column.header }}
          </span>
        </template>
        <template #body="{ data, field }">
          <div v-if="field === 'createdDate'">
            {{ convertTimeStampToDate(data.createdDate) }}
          </div>
          <div v-else-if="field === 'changeField'" class="change-field">
            <div>
              <div v-if="!checkChangeContent(data.oldValue) && !checkChangeContent(data.newValue)">
                {{ data.changeField }}
              </div>
              <div v-else @click="openViewMoreHistory(data.oldValue, data.newValue, data.changeAction)" class="view-more">
                ...
                <span class="view-more text-blue-600 font-bold underline cursor-pointer text-left uppercase">{{ $t('management.log.view_more') }}</span>
              </div>
            </div>
          </div>
          <div v-else>
            {{ data[field] }}
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <Button id="btn-close" :label="$t('common.buttons.ok')" class="import-button" @click="onClose()" />
  </div>
</template>
